import { DataTable } from '../blocks/DataTable';
import { DataTableToolbar } from '../blocks/data-table/DataTableToolbar';
import type { ColumnDef } from '@tanstack/react-table';
import { AdminShell, WorksurfaceHeader, RegistryWorkspace, RegistryTableSurface, RegistryStatus } from '../blocks';

export interface SalesLeadRow {
  lead_id: string;
  name: string;
  email: string;
  phone: string;
  company: string;
  address: string;
  eligibility_status: string;
  storefront_key: string;
  created_at: string;
}

export interface SalesLeadsWorkspaceProps {
  leads: SalesLeadRow[];
  total: number;
  loading: boolean;
  error?: string;
  search: string;
  onSearchChange: (value: string) => void;
  pagination: { pageIndex: number; pageSize: number };
  onPaginationChange: (value: { pageIndex: number; pageSize: number } | ((current: { pageIndex: number; pageSize: number }) => { pageIndex: number; pageSize: number })) => void;
  onRetry: () => void;
}

const columns: ColumnDef<SalesLeadRow>[] = [
  { accessorKey: 'name', header: 'Name', enableSorting: false },
  { accessorKey: 'email', header: 'Email', enableSorting: false },
  { accessorKey: 'phone', header: 'Phone', enableSorting: false, cell: ({ row }) => row.original.phone || '—' },
  { accessorKey: 'company', header: 'Company', enableSorting: false, cell: ({ row }) => row.original.company || '—' },
  { accessorKey: 'address', header: 'Service address', enableSorting: false },
  { accessorKey: 'eligibility_status', header: 'Eligibility', enableSorting: false, cell: ({ row }) => <RegistryStatus variant={row.original.eligibility_status === 'serviceable' ? 'success' : 'muted'} label={row.original.eligibility_status.replaceAll('_', ' ')} /> },
  { accessorKey: 'storefront_key', header: 'Storefront', enableSorting: false },
  { accessorKey: 'created_at', header: 'Requested', enableSorting: false, cell: ({ row }) => new Date(row.original.created_at).toLocaleString() },
];

export function SalesLeadsWorkspace(props: SalesLeadsWorkspaceProps) {
  return <AdminShell title={<WorksurfaceHeader parentLabel="Sales" activeLabel="Leads" description="Visitors who asked to be contacted about service at their address." />} bodyClassName="flex min-h-0 flex-col" flat>
    <RegistryWorkspace>
      {props.error ? <div role="alert" className="flex items-center gap-3 border-b border-border p-4 text-sm text-foreground">
        <span>{props.error}</span>
        <button type="button" onClick={props.onRetry} className="rounded-md border border-border px-3 py-2 text-foreground hover:bg-muted focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring">Retry</button>
      </div> : null}
      <RegistryTableSurface>
        <DataTable mode="manual" columns={columns} data={props.leads} loading={props.loading} manualPagination totalRowCount={props.total}
          pagination={props.pagination} onPaginationChange={props.onPaginationChange} showPagination className="rounded-none border-none"
          emptyState={<div className="p-8 text-center text-sm text-muted-foreground">{props.error ? 'Lead data is unavailable.' : props.search ? 'No leads match your search.' : 'No contact requests yet.'}</div>}
          renderToolbar={(table) => <DataTableToolbar table={table} searchValue={props.search} onSearchChange={props.onSearchChange} searchPlaceholder="Search leads…" resultCount={props.total} />}
        />
      </RegistryTableSurface>
    </RegistryWorkspace>
  </AdminShell>;
}
